"use client"

/**
 * Khung hiển thị (display window) — gộp "lên lịch xuất bản" + "lên lịch hủy
 * xuất bản" thành một khái niệm: nội dung hiển thị TỪ `start` ĐẾN `end`.
 *
 * - start = null  → hiển thị ngay khi lưu / đăng
 * - end   = null  → hiển thị vô thời hạn (không tự gỡ)
 *
 * Dùng chung cho Post (trong modal Xuất bản) và Letáky (inline trong panel).
 * Cron phía Strapi (`scheduledVisibility`) thực thi cả hai mốc.
 */

import { t } from "@/languages/i18n"
import { Button, DatePicker, Typography } from "antd"
import dayjs from "dayjs"
import { ReactNode } from "react"

const { Text } = Typography

export interface DisplayWindow {
  start: string | null
  end: string | null
}

const PICKER_FORMAT = "YYYY-MM-DD HH:mm"
export const formatWindowDate = (iso: string) => dayjs(iso).format("HH:mm DD/MM/YYYY")

/** Trả về thông báo lỗi, hoặc null nếu khung hợp lệ. */
export const validateDisplayWindow = ({ start, end }: DisplayWindow): string | null => {
  const now = dayjs()
  if (start && !dayjs(start).isAfter(now)) {
    return t("Thời điểm bắt đầu hiển thị phải ở trong tương lai")
  }
  if (end && !dayjs(end).isAfter(now)) {
    return t("Thời điểm tự động gỡ phải ở trong tương lai")
  }
  if (start && end && !dayjs(end).isAfter(dayjs(start))) {
    return t("Thời điểm tự động gỡ phải sau thời điểm bắt đầu hiển thị")
  }
  return null
}

/** Mô tả một dòng cho panel trạng thái. */
export const describeDisplayWindow = ({ start, end }: DisplayWindow): ReactNode => {
  if (start && end) {
    return (
      <>
        {t("Hiển thị từ")} <strong>{formatWindowDate(start)}</strong> {t("đến")}{" "}
        <strong>{formatWindowDate(end)}</strong>
      </>
    )
  }
  if (start) {
    return (
      <>
        {t("Hiển thị từ")} <strong>{formatWindowDate(start)}</strong>, {t("không tự gỡ")}
      </>
    )
  }
  if (end) {
    return (
      <>
        {t("Đang hiển thị, tự động gỡ lúc")} <strong>{formatWindowDate(end)}</strong>
      </>
    )
  }
  return null
}

interface DisplayWindowFieldsProps {
  value: DisplayWindow
  onChange: (next: DisplayWindow) => void
  /** Ẩn ô bắt đầu (vd. bài đã live, chỉ chỉnh mốc gỡ). */
  hideStart?: boolean
  /** Mốc gỡ bắt buộc (vd. modal "Lịch gỡ bài"): đổi placeholder, hiện dấu *. */
  endRequired?: boolean
  startPlaceholder?: string
  /** Gợi ý nhanh (vd. ngày bắt đầu / kết thúc của letak). Bỏ qua nếu đã ở quá khứ. */
  startSuggestion?: { label: string; value: string } | null
  endSuggestion?: { label: string; value: string } | null
  /** Lỗi hiển thị ngay dưới ô "Tự động gỡ lúc". */
  endError?: string | null
}

const SuggestionLink = ({
  suggestion,
  current,
  onPick,
}: {
  suggestion?: { label: string; value: string } | null
  current: string | null
  onPick: (value: string) => void
}) => {
  if (!suggestion || suggestion.value === current || !dayjs(suggestion.value).isAfter(dayjs())) return null
  return (
    <Button type="link" size="small" className="!px-0" onClick={() => onPick(suggestion.value)}>
      {suggestion.label} ({formatWindowDate(suggestion.value)})
    </Button>
  )
}

const disablePast = (current: dayjs.Dayjs) => !!current && current < dayjs().startOf("day")

export const DisplayWindowFields = ({
  value,
  onChange,
  hideStart = false,
  endRequired = false,
  startPlaceholder,
  startSuggestion,
  endSuggestion,
  endError,
}: DisplayWindowFieldsProps) => {
  const minEnd = value.start ? dayjs(value.start).startOf("day") : dayjs().startOf("day")

  return (
    <div className="flex flex-col gap-3 w-full">
      {!hideStart && (
        <div>
          <Text className="block mb-1" style={{ fontSize: 13, fontWeight: 500 }}>
            {t("Bắt đầu hiển thị")}
          </Text>
          <DatePicker
            showTime
            className="w-full"
            format={PICKER_FORMAT}
            placeholder={startPlaceholder ?? t("Để trống = hiển thị ngay")}
            value={value.start ? dayjs(value.start) : null}
            onChange={date => onChange({ ...value, start: date ? date.toISOString() : null })}
            disabledDate={disablePast}
          />
          <SuggestionLink
            suggestion={startSuggestion}
            current={value.start}
            onPick={start => onChange({ ...value, start })}
          />
        </div>
      )}
      <div>
        <Text className="block mb-1" style={{ fontSize: 13, fontWeight: 500 }}>
          {t("Tự động gỡ lúc")}
          {endRequired && <span className="text-red-500 ml-0.5">*</span>}
        </Text>
        <DatePicker
          showTime
          className="w-full"
          format={PICKER_FORMAT}
          placeholder={endRequired ? t("Chọn thời điểm gỡ bài") : t("Để trống = không tự gỡ")}
          value={value.end ? dayjs(value.end) : null}
          onChange={date => onChange({ ...value, end: date ? date.toISOString() : null })}
          disabledDate={current => !!current && current < minEnd}
          status={endError ? "error" : undefined}
        />
        {endError && (
          <Text type="danger" className="block mt-1" style={{ fontSize: 12 }}>
            {endError}
          </Text>
        )}
        <SuggestionLink
          suggestion={endSuggestion}
          current={value.end}
          onPick={end => onChange({ ...value, end })}
        />
      </div>
    </div>
  )
}
